import { useMemo } from "react";
import { keepPreviousData, type QueryKey, useInfiniteQuery } from "@tanstack/react-query";
import type { Paginated } from "@milqit/shared-types";

/** Rows per request when a list loads more. */
export const PAGE_SIZE = 25;
/** The API's largest page; exports and "every row" reads walk the list in pages this size. */
export const MAX_PAGE_SIZE = 100;

export type FetchPage<R> = (paging: { page: number; pageSize: number }) => Promise<R>;

/** A row of a page type: `RowOf<Paginated<Order>>` is `Order`. */
type RowOf<R extends Paginated<unknown>> = R["items"][number];

/**
 * Every row of a paged list, one maximum-size page at a time (CSV exports, and the few screens
 * that genuinely need all of something, such as one morning's deliveries). `onProgress` reports
 * rows fetched so far and the total.
 */
export async function fetchAllPages<T>(
  fetchPage: FetchPage<Paginated<T>>,
  onProgress?: (fetched: number, total: number) => void,
): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 1; ; page += 1) {
    const result = await fetchPage({ page, pageSize: MAX_PAGE_SIZE });
    rows.push(...result.items);
    onProgress?.(rows.length, result.total);
    if (result.page >= result.totalPages || !result.items.length) return rows;
  }
}

/** Joins loaded pages, dropping a row that moved onto a later page while staff were reading. */
export function flattenPages<T>(pages: readonly Paginated<T>[], getRowId: (row: T) => string): T[] {
  const seen = new Set<string>();
  const rows: T[] = [];
  for (const page of pages) {
    for (const row of page.items) {
      const id = getRowId(row);
      if (seen.has(id)) continue;
      seen.add(id);
      rows.push(row);
    }
  }
  return rows;
}

export const nextPageAfter = <T>(last: Paginated<T>) =>
  last.page < last.totalPages ? last.page + 1 : undefined;

interface PagedListOptions<R extends Paginated<unknown>> {
  /** From the area's key factory, including every filter, so changing one starts a fresh list. */
  queryKey: QueryKey;
  fetchPage: FetchPage<R>;
  getRowId: (row: RowOf<R>) => string;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * A server-filtered list that loads more as staff scroll (TanStack `useInfiniteQuery`). The first
 * page also carries the total and, for tabbed lists, each tab's count.
 */
export function usePagedList<R extends Paginated<unknown>>({
  queryKey,
  fetchPage,
  getRowId,
  pageSize = PAGE_SIZE,
  enabled = true,
}: PagedListOptions<R>) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage({ page: pageParam, pageSize }),
    initialPageParam: 1,
    getNextPageParam: (last: R) => nextPageAfter(last),
    // Changing a filter keeps the old rows on screen until the new ones arrive.
    placeholderData: keepPreviousData,
    enabled,
  });
  const pages = query.data?.pages;
  const first = pages?.[0];
  // A new array only when the pages change: the table rebuilds its rows whenever `data` does.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getRowId is a stable field reader
  const rows = useMemo(() => flattenPages<RowOf<R>>(pages ?? [], getRowId), [pages]);
  return {
    rows,
    total: first?.total ?? 0,
    /** The first page as the API sent it (tab counts live here for tabbed lists). */
    first,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    /** True while new filters load over the previous rows. */
    isRefreshing: query.isPlaceholderData,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => query.fetchNextPage(),
    refetch: () => query.refetch(),
  };
}

/** What `usePagedList` returns, for components that render any list (rows of type `T`). */
export type PagedList<T> = Omit<ReturnType<typeof usePagedList<Paginated<T>>>, "rows"> & {
  rows: T[];
};
